import React, { ChangeEvent, CSSProperties, useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { ContactOwnersService } from '@services/ContactOwners';
import Label from '@components/inputs/text/Label';
import DatePicker from '@components/inputs/DateInput';
import DropDownMenu, { IChoice } from '@components/inputs/DropDownMenu';
import useServiceHook from '@hooks/useServiceHook';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import {
    addWeeks,
    dateToISOFormat,
    emailMinusSplendDomain
} from '@utils/misc/functions';
import { NavBar } from "@components/navigation/NavBar/NavBar";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { OnboardingAgreementState, OnboardingAgreementAction } from './onboardingAgreementReducer';
import { Country } from "@utils/constants/localisation";

interface FlexiOwnAgreementScheduleV2Props {
  navigateTarget: string;
  onboardingAgreementState: OnboardingAgreementState;
  onboardingAgreementDispatch: React.Dispatch<OnboardingAgreementAction>;
  country: Country;
}

export default function FlexiOwnAgreementScheduleV2({
  navigateTarget,
  onboardingAgreementState,
  onboardingAgreementDispatch,
  country
}: FlexiOwnAgreementScheduleV2Props) {
  const navigate = useNavigate();
  const msalContext = useMsal();
  const todaysDate = dateToISOFormat(new Date());  // YYYY-mm-dd

  const [executionDate, setExecutionDate] = useState<string>(onboardingAgreementState.executionDate || todaysDate);
  const [subsStartDate, setSubsStartDate] = useState<string>(onboardingAgreementState.subsStartDate || todaysDate);

  // Retrieves all available zoho users for each country
  const [userLoading, , , availableContactOwners] = useServiceHook(
    ContactOwnersService.getContactOwners,
    [country],
    true,
  );

  let dropdown = [];
  let dropdownDefault: Partial<IChoice | undefined> = undefined;

  // Set default contact owner from availableContactOwner list
  if (availableContactOwners) {
    // First try to match existing state (if user already selected)
    const defaultContactOwnerIndex = availableContactOwners.findIndex((index: any) =>
      emailMinusSplendDomain(index.email) === emailMinusSplendDomain(onboardingAgreementState.contactOwnerEmail));

    if (defaultContactOwnerIndex >= 0) {
      const selectedContact = availableContactOwners[defaultContactOwnerIndex];
      dropdownDefault = { label: selectedContact.label, value: selectedContact.id, enabled: true };
    } else if (availableContactOwners.length > 0) {
      // Try logged-in user, then fall back to first contact owner
      const loggedInUserEmail = msalContext.instance.getActiveAccount()?.username;
      const loggedInContact = loggedInUserEmail
        ? availableContactOwners.find((c: any) =>
            emailMinusSplendDomain(c.email) === emailMinusSplendDomain(loggedInUserEmail))
        : null;
      const defaultContact = loggedInContact || availableContactOwners[0];
      dropdownDefault = { label: defaultContact.label, value: defaultContact.id, enabled: true };
    }

    dropdown = availableContactOwners.map((item: any) => ({
      value: item.id,
      label: item.label,
      enabled: true
    }));
  }

  const onContactOwnerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.currentTarget.value;
    const selectedLabel = e.currentTarget.options[e.currentTarget.selectedIndex].label;
    const selectedContact = availableContactOwners?.find((contact: any) => contact.id === selectedId);

    onboardingAgreementDispatch({ type: "SET_CONTACT_OWNER", payload: selectedLabel });
    onboardingAgreementDispatch({ type: "SET_CONTACT_OWNER_ID", payload: selectedId });
    if (selectedContact?.email) {
      onboardingAgreementDispatch({ type: "SET_CONTACT_OWNER_EMAIL", payload: selectedContact.email });
    }
  };

  useEffect(() => {
    // Initialize dates on first render if not already set
    if (onboardingAgreementState.executionDate === '') {
      onboardingAgreementDispatch({ type: "SET_EXECUTION_DATE", payload: executionDate });
    }

    if (onboardingAgreementState.subsStartDate === '') {
      onboardingAgreementDispatch({ type: "SET_SUBS_START_DATE", payload: subsStartDate });
    }
  }, []);

  useEffect(() => {
    // Update context's subsStartDate field upon state update
    onboardingAgreementDispatch({ type: "SET_SUBS_START_DATE", payload: subsStartDate });
  }, [subsStartDate]);

  useEffect(() => {
    // Update context's executionDate field upon state update
    onboardingAgreementDispatch({ type: "SET_EXECUTION_DATE", payload: executionDate });
  }, [executionDate]);

  useEffect(() => {
    // When subsStartDate changes, update the subsEndDate accordingly
    // reducedTermLengthWeeks should be populated from CBPOnboard data
    if (onboardingAgreementState.reducedTermLengthWeeks && subsStartDate) {
      const startDate = new Date(subsStartDate);
      const endDate = addWeeks(startDate, onboardingAgreementState.reducedTermLengthWeeks);
      onboardingAgreementDispatch({ type: "SET_SUBS_END_DATE", payload: dateToISOFormat(endDate) });
    }
  }, [subsStartDate, onboardingAgreementState.reducedTermLengthWeeks]);

  // Set default contact owner when availableContactOwners is loaded
  useEffect(() => {
    if (availableContactOwners && availableContactOwners.length > 0) {
      // Only set default if not already set
      if (!onboardingAgreementState.contactOwnerId || !onboardingAgreementState.contactOwnerEmail) {
        // Try to find logged-in user first
        const loggedInUserEmail = msalContext.instance.getActiveAccount()?.username;
        const loggedInContact = loggedInUserEmail
          ? availableContactOwners.find((c: any) =>
              emailMinusSplendDomain(c.email) === emailMinusSplendDomain(loggedInUserEmail))
          : null;

        // Fall back to first contact if logged-in user not in list
        const defaultContact = loggedInContact || availableContactOwners[0];

        onboardingAgreementDispatch({ type: "SET_CONTACT_OWNER", payload: defaultContact.label });
        onboardingAgreementDispatch({ type: "SET_CONTACT_OWNER_ID", payload: defaultContact.id });
        onboardingAgreementDispatch({ type: "SET_CONTACT_OWNER_EMAIL", payload: defaultContact.email });
      }
    }
  }, [availableContactOwners]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };

  const divStyles: CSSProperties = {
    margin: '2.5vh 0vw',
  };

  return (
    <>
      <NavBar pageTitle={"Agreement Schedule"} backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={onSubmit}>
        <div style={divStyles}>
          <p style={{ margin: '2.5vh 0' }}>Please ensure the key terms for the customer are correct.</p>
          <Label text='Contact Owner/Authorised Signatory' requiredFieldInd={true} />
          {userLoading === false || userLoading === null ? (
            <DropDownMenu
              menuName={'contactOwnerDropMenu'}
              defaultVal={dropdownDefault}
              required={true}
              choices={dropdown}
              onSelect={(event: ChangeEvent<HTMLSelectElement>) => onContactOwnerChange(event)}
            />
          ) : (
            <Elliptical />
          )}
        </div>
        <div style={divStyles}>
          <Label text='Contract execution date' requiredFieldInd={true} />
          <DatePicker
            name='contractExeDate'
            defaultVal={executionDate}
            min={todaysDate}
            required={true}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setExecutionDate(e.currentTarget.value)}
          />
        </div>
        <div style={divStyles}>
          <Label text='Subscription start date' requiredFieldInd={true} />
          <DatePicker
            name='contractStartDate'
            defaultVal={subsStartDate}
            min={todaysDate}
            required={true}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSubsStartDate(e.currentTarget.value)}
          />
        </div>
        <SubmitBtn text={"Next"} disabled={!onboardingAgreementState.contactOwnerId} />
      </form>
    </>
  );
}